<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import KycCapture from 'dashboard/components-next/kyc/KycCapture.vue';
import OfferKycHistory from './OfferKycHistory.vue';
import {
  kycStatusClass,
  kycStatusLabel,
} from '../utils/offerContractPresenter';

defineProps({
  selectedKycVerification: { type: Object, default: null },
  selectedKycVerifications: { type: Array, default: () => [] },
  selectedActiveKycVerification: { type: Object, default: null },
  selectedKycProviderError: { type: String, default: '' },
  canSubmitKycVerification: { type: Boolean, default: false },
  kycActiveAttemptMessage: { type: String, default: '' },
  kycSubmitLabel: { type: String, default: '' },
  kycAction: { type: String, default: '' },
  showKycCapture: { type: Boolean, default: false },
  documentType: { type: String, default: 'CNH' },
  consentAccepted: { type: Boolean, default: false },
  kycCapturePayload: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'update:documentType',
  'update:consentAccepted',
  'toggleCapture',
  'captured',
  'failed',
  'unsupported',
  'refresh',
  'submit',
]);

const handleKycCaptured = payload => emit('captured', payload);
const handleKycCaptureFailure = payload => emit('failed', payload);
const handleKycUnsupported = () => emit('unsupported');
const refreshKycVerifications = () => emit('refresh');
const submitKycVerification = () => emit('submit');
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="rounded-lais border border-lais-border bg-white p-4">
    <div class="mb-3 flex items-center justify-between gap-3">
      <h4 class="font-redHatDisplay text-[16px] font-semibold text-lais-text">
        KYC do interessado
      </h4>
      <span
        class="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
        :class="kycStatusClass(selectedKycVerification?.status)"
      >
        {{ kycStatusLabel(selectedKycVerification?.status) }}
      </span>
    </div>

    <div
      v-if="selectedKycProviderError"
      role="alert"
      class="mb-3 rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-700"
    >
      <p class="font-semibold">Erro provider KYC</p>
      <p class="mt-1">{{ selectedKycProviderError }}</p>
    </div>

    <OfferKycHistory :verifications="selectedKycVerifications" />

    <div
      v-if="selectedActiveKycVerification"
      role="status"
      class="my-3 rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800"
    >
      {{ kycActiveAttemptMessage }}
    </div>

    <div class="mt-3 grid gap-3">
      <label class="grid gap-1 text-[12px] font-semibold text-lais-text-muted">
        Tipo de documento
        <select
          :value="documentType"
          :disabled="!canSubmitKycVerification"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          @change="emit('update:documentType', $event.target.value)"
        >
          <option value="CNH">CNH</option>
          <option value="RG">RG</option>
          <option value="CPF">CPF</option>
          <option value="NEW_RG">Novo RG</option>
        </select>
      </label>

      <label
        class="flex items-start gap-2 rounded-lais-sm border border-lais-border-soft p-3 text-[12px] leading-relaxed text-lais-text-body"
      >
        <input
          :checked="consentAccepted"
          type="checkbox"
          :disabled="!canSubmitKycVerification"
          class="mt-0.5 size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
          @change="emit('update:consentAccepted', $event.target.checked)"
        />
        <span>
          Confirmo consentimento LGPD para validação biométrica e envio ao motor
          de KYC configurado.
        </span>
      </label>

      <div class="flex flex-wrap gap-2 text-[12px]">
        <span
          class="rounded-full px-2 py-1 font-semibold"
          :class="
            kycCapturePayload.selfie_base64
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-lais-bg-soft text-lais-text-muted'
          "
        >
          Selfie
        </span>
        <span
          class="rounded-full px-2 py-1 font-semibold"
          :class="
            kycCapturePayload.document_base64
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-lais-bg-soft text-lais-text-muted'
          "
        >
          Documento
        </span>
        <span
          v-if="kycCapturePayload.document_back_base64"
          class="rounded-full bg-emerald-50 px-2 py-1 font-semibold text-emerald-700"
        >
          Verso
        </span>
      </div>

      <KycCapture
        v-if="showKycCapture"
        :document-type="documentType"
        :disabled="kycAction === 'submit' || !canSubmitKycVerification"
        @captured="handleKycCaptured"
        @failed="handleKycCaptureFailure"
        @unsupported="handleKycUnsupported"
      />

      <div class="flex flex-wrap gap-2">
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-scan-face"
          :disabled="!canSubmitKycVerification"
          @click="emit('toggleCapture')"
        >
          {{ showKycCapture ? 'Ocultar captura' : 'Capturar' }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="kycAction === 'sync'"
          @click="refreshKycVerifications"
        >
          Sincronizar KYC
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          icon="i-lucide-shield-check"
          :is-loading="kycAction === 'submit'"
          :disabled="!canSubmitKycVerification"
          @click="submitKycVerification"
        >
          {{ kycSubmitLabel }}
        </LaisPrimaryButton>
      </div>
    </div>
  </section>
</template>
